import React, { useRef, useImperativeHandle, useEffect, useMemo } from "react";
import styled from "styled-components";

const InputNumberStyle = styled.div`
    width: 100%;
`;

const ContainerStyle = styled.div`
    height: 26px;
    display: flex;
    border-radius: 2px;
    box-sizing: border-box;
    background-color: ${props => props.theme.white};
    border: 1px solid ${props => props.theme.greyLight};
    &:focus-within {
        border-color: ${props => props.theme.white};
        outline: 1px solid ${props => props.theme.primaryLight};
    }
    &.disabled {
        pointer-events: none;
        background-color: ${props => props.theme.greyLighter};
    }
    &.error {
        background-color: ${props => props.theme.redLight};
        border-color: ${props => props.theme.redDark};
        &:focus-within {
            border-color: ${props => props.theme.redLight};
            outline: 1px solid ${props => props.theme.redDarker};
        }
    }
`;

const TitleStyle = styled.p`
    height: 12px;
    font-size: 12px;
    user-select: none;
    margin-bottom: 1px;
    letter-spacing: 0.4px;
    color: ${props => props.theme.black};
`;

const InputStyle = styled.input`
    width: 100%;
    border: none;
    display: block;
    font-weight: 400;
    box-sizing: border-box;
    padding: 3px 5px 2px 10px;
    background-color: transparent;
    color: ${props => props.theme.black};
    text-align: ${props => props.$textAlign};
    &::placeholder {
        color: ${props => props.theme.greyLight};
    }
`;

const SymbolStyle = styled.div`
    height: 25px;
    display: flex;
    padding: 0 7px;
    user-select: none;
    align-items: center;
    box-sizing: border-box;
    border-left: 1px solid ${props => props.theme.greyLight};
    &.error {
        border-color: ${props => props.theme.redDark};
    }
`;

const ErrorStyle = styled.p`
    height: 0;
    opacity: 0;
    font-size: 11px;
    visibility: hidden;
    color: ${props => props.theme.redDark};
    transition: opacity 0.3s, height 0.3s, visibility 0.3s;
    &.error {
        opacity: 1;
        height: 11px;
        margin-top: 2px;
        visibility: visible;
    }
`;

export const InputNumber = props => {

    const { id, ref, name, title, value, fractionDigits = 0, symbol, disabled, maxLength, placeholder, textAlign = "left", error, errorVisible } = props;

    const inputRef = useRef(null);
    const lastValidInputRef = useRef("");
    const nf = useMemo(() => new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
        useGrouping: false
    }), [fractionDigits]);
    const inputPattern = useMemo(() => {
        if (fractionDigits > 0) {
            return new RegExp(`^-?\\d*(?:,\\d{0,${fractionDigits}})?$`);
        }
        return /^-?\d*$/;
    }, [fractionDigits]);
    const isValidInput = value =>
        value === "" || value === "-" || inputPattern.test(value);

    const errorName = name ? name + "ErrorText" : "errorText";

    const disabledClassName = disabled ? "disabled" : "";
    const errorClassName = (errorVisible && error) ? "error" : "";
    const className = [disabledClassName, errorClassName].join(" ");

    useImperativeHandle(ref, () => ({
        get hasFocus() {
            return inputRef.current === document.activeElement;
        },
        get value() {
            return inputRef.current.value;
        },
        focus: () => inputRef.current.focus(),
        writeValue: val => (inputRef.current.value = toString(val)),
        addEventListener: (type, listener) => inputRef.current.addEventListener(type, listener),
        removeEventListener: (type, listener) => inputRef.current?.removeEventListener(type, listener)
    }), [inputRef, nf]);

    useEffect(() => {
        if (value !== toValue(inputRef.current.value)) {
            if (document.activeElement !== inputRef.current) {
                inputRef.current.value = toString(value);
            }
        }
        lastValidInputRef.current = inputRef.current.value;
    }, [value, nf]);

    const isBlank = value => (value === null || value === undefined || /^\s*$/.test(value));
    const toString = value => (isBlank(value) ? "" : nf.format(value));
    const toValue = value => ((isBlank(value) || value === "-") ? null : Number(value.replace(",", ".")));
    const onChange = event => {
        const inputValue = event.target.value;
        if (!isValidInput(inputValue)) {
            event.target.value = lastValidInputRef.current;
            return;
        }
        lastValidInputRef.current = inputValue;
        props.onChange(toValue(inputValue));
    };

    const allowedKey = event => {
        const { key, ctrlKey } = event;
        const allowed = ["Backspace", "Delete", "ArrowUp", "ArrowLeft", "ArrowRight", "ArrowDown", "Home", "End", "Tab"];
        if (ctrlKey && ["X", "V", "C"].includes(key.toUpperCase())) {
            return true;
        }
        return allowed.includes(key);
    };

    const onKeyDown = event => {
        const { key } = event;
        if (!allowedKey(event) && key.length === 1) {
            const {target} = event;
            const currentValue = target.value;
            const selectionStart = target.selectionStart ?? currentValue.length;
            const selectionEnd = target.selectionEnd ?? selectionStart;

            const nextValue =
                currentValue.substring(0, selectionStart)
                + key
                + currentValue.substring(selectionEnd);

            if (!isValidInput(nextValue)) {
                event.preventDefault();
            }
        }

        if (props.onKeyDown) {
            props.onKeyDown(event);
        }
    };

    return (
        <InputNumberStyle>
            {title !== false &&
                <TitleStyle>{title}</TitleStyle>
            }
            <ContainerStyle className={className}>
                <InputStyle
                    id={id}
                    name={name}
                    type="text"
                    ref={inputRef}
                    onChange={onChange}
                    disabled={disabled}
                    maxLength={maxLength}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    className={errorClassName}
                    $textAlign={textAlign} />
                {symbol &&
                    <SymbolStyle className={className}>
                        {symbol}
                    </SymbolStyle>
                }
            </ContainerStyle>
            <ErrorStyle
                name={errorName}
                className={typeof error === "string" ? errorClassName : ""}>
                {error}
            </ErrorStyle>
        </InputNumberStyle>
    );
};